/**
 * GridCrash — Board UI
 *
 * Native iOS (Capacitor) changes:
 * - Long-press flagging removed: touch flags via the Dig/Flag action
 *   bar (setFlagMode) — desktop right-click and pen barrel button remain
 * - PENCIL FIX: resolve cell coords BEFORE calling e.preventDefault()
 *   so pencil taps on child elements (SVG icons, number spans) are caught
 * - Expert mode: adds .board-grid--expert class when cols >= 25
 *   so CSS can target tighter sizing without media query conflicts,
 *   plus a pinch-to-zoom viewport (PinchZoom) on the scroll container
 * - Hints footer: exported detectInputType() lets main.ts swap
 *   keyboard hints for touch hints dynamically on first pointer event
 */

import { Capacitor } from "@capacitor/core";
import type { GameState, Cell, GamePhase } from "../game/engine.js";
import { getActiveTheme } from "../game/board_themes.js";
import { hapticLight, hapticMedium, hapticHeavy } from "../game/haptics.js";
import { PinchZoom } from "./pinch_zoom.js";

// ─── Types ────────────────────────────────────────────────────────────────────

type RevealCallback = (row: number, col: number) => void;
type FlagCallback   = (row: number, col: number) => void;
type SoundCallback  = (sound: "flag" | "cascade") => void;

interface BoardCallbacks {
  onReveal: RevealCallback;
  onFlag:   FlagCallback;
  onSound?: SoundCallback;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * How far the pointer can drift before a tap is cancelled (px).
 * Pencil/finger can wobble — this tolerates small movement.
 */
const MOVE_THRESHOLD_PX = 10;

// ─── Input type detection ─────────────────────────────────────────────────────

/**
 * Detect the primary input type for this device.
 * Used by main.ts to render appropriate hint footer.
 *
 * "touch"  → finger touch (phone/tablet)
 * "pen"    → Apple Pencil or stylus
 * "mouse"  → desktop mouse
 *
 * Detection strategy:
 * 1. CSS media query `pointer: coarse` = touch/pen primary
 * 2. CSS media query `hover: none` = no hover capability = touch device
 * 3. `navigator.maxTouchPoints` > 1 = multi-touch = phone/tablet
 *
 * Note: iPad with Pencil reports both `pointer: fine` (pencil) and
 * `pointer: coarse` (finger) via `any-pointer: coarse`.
 * We treat iPad as "touch" since most interactions will be finger/pencil.
 */
export function detectInputType(): "touch" | "mouse" {
  // Native iOS shell: always touch, regardless of what the WebView's
  // pointer/hover media queries report.
  if (Capacitor.isNativePlatform()) {
    return "touch";
  }

  const coarse  = window.matchMedia("(pointer: coarse)").matches;
  const anyCoarse = window.matchMedia("(any-pointer: coarse)").matches;
  const noHover = window.matchMedia("(hover: none)").matches;
  const touchPoints = navigator.maxTouchPoints ?? 0;

  if (coarse || anyCoarse || noHover || touchPoints > 1) {
    return "touch";
  }
  return "mouse";
}

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const FLAG_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
  <path d="M4 21V4l9 3.5L4 11V4"/>
  <line x1="4" y1="4" x2="4" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
</svg>`;

const MINE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
  <circle cx="12" cy="12" r="5"/>
  <line x1="12" y1="2" x2="12" y2="6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="2" y1="12" x2="6" y2="12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="18" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <line x1="19.07" y1="4.93" x2="16.24" y2="7.76" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  <line x1="7.76" y1="16.24" x2="4.93" y2="19.07" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
</svg>`;

const WRONG_FLAG_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false">
  <line x1="18" y1="6" x2="6" y2="18"/>
  <line x1="6" y1="6" x2="18" y2="18"/>
</svg>`;

// ─── Board Class ──────────────────────────────────────────────────────────────

export class BoardUI {
  private container: HTMLElement;
  private callbacks: BoardCallbacks;

  // Grid state
  private rows  = 0;
  private cols  = 0;
  private phase: GamePhase = "IDLE";

  // Keyboard focus
  private focusRow = 0;
  private focusCol = 0;

  // Tap mode — "flag" set by the mobile action bar (Dig/Flag toggle)
  private flagMode = false;

  // Expert-board pinch-to-zoom viewport
  private pinchZoom: PinchZoom | null = null;

  // Pointer / tap state
  private pressStartCell:   [number, number] | null = null;
  private pressStartX      = 0;
  private pressStartY      = 0;
  private pressMoved       = false;
  private activePointerId: number | null = null;

  // Cell DOM cache
  private cellElements: HTMLElement[][] = [];

  // Pencil hover highlight (iPadOS 16.4+ hover-capable Pencils)
  private hoverCellEl: HTMLElement | null = null;

  constructor(container: HTMLElement, callbacks: BoardCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
    this.bindContainerEvents();
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  initialize(state: Readonly<GameState>): void {
    this.rows = state.difficulty.rows;
    this.cols = state.difficulty.cols;
    this.phase = state.phase;
    this.focusRow = 0;
    this.focusCol = 0;
    this.buildGrid(state);
  }

  update(state: Readonly<GameState>): void {
    const prevPhase = this.phase;
    this.phase = state.phase;

    if (
      state.difficulty.rows !== this.rows ||
      state.difficulty.cols !== this.cols
    ) {
      this.initialize(state);
      return;
    }

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        this.renderCell(state.grid[r][c], r, c, state.phase);
      }
    }

    if (state.phase === "LOSE" && prevPhase !== "LOSE") {
      this.shakeGrid();
    }

    this.updateInteractivity(state.phase);
  }

  focusCell(row: number, col: number): void {
    this.setFocusedCell(row, col);
  }

  /**
   * Dig/Flag toggle (mobile action bar). In flag mode a plain tap
   * places or removes a flag instead of revealing.
   */
  setFlagMode(on: boolean): void {
    this.flagMode = on;
    this.container.classList.toggle("board-grid--flag-mode", on);
  }

  /** Aftershock tremor (6C): rattle the grid and flash the numbers. */
  tremorShake(): void {
    this.container.classList.remove("board-grid--tremor");
    void this.container.offsetWidth;
    this.container.classList.add("board-grid--tremor");
    this.container.addEventListener(
      "animationend",
      () => this.container.classList.remove("board-grid--tremor"),
      { once: true }
    );
    hapticHeavy();
  }

  /** Briefly highlight a hint-revealed cell so the player sees what they paid for. */
  pulseHint(row: number, col: number): void {
    const cellEl = this.cellElements[row]?.[col];
    if (!cellEl) return;
    cellEl.classList.remove("board-cell--hint");
    void cellEl.offsetWidth;
    cellEl.classList.add("board-cell--hint");
    cellEl.addEventListener(
      "animationend",
      () => cellEl.classList.remove("board-cell--hint"),
      { once: true }
    );
    hapticLight();
  }

  // ─── Grid Construction ───────────────────────────────────────────────────────

  private buildGrid(state: Readonly<GameState>): void {
    this.container.innerHTML = "";
    this.container.className = "board-grid";

    // Expert class for tighter CSS sizing (cols >= 25), plus a
    // pinch-to-zoom viewport on the surrounding scroll container.
    const viewport = this.container.parentElement;
    const isExpert = this.cols >= 25;
    if (isExpert) {
      this.container.classList.add("board-grid--expert");
    }
    if (viewport) {
      viewport.classList.toggle("board-viewport--zoomable", isExpert);
      if (isExpert && !this.pinchZoom) {
        this.pinchZoom = new PinchZoom(viewport, this.container);
      } else if (!isExpert && this.pinchZoom) {
        this.pinchZoom.destroy();
        this.pinchZoom = null;
      }
    }
    this.pinchZoom?.reset();

    this.container.setAttribute("role", "grid");
    this.container.setAttribute(
      "aria-label",
      `GridCrash board, ${this.rows} rows, ${this.cols} columns`
    );
    this.container.style.setProperty("--cols", String(this.cols));

    this.cellElements = [];

    for (let r = 0; r < this.rows; r++) {
      const rowEl = document.createElement("div");
      rowEl.setAttribute("role", "row");
      rowEl.className = "board-row";
      const rowCells: HTMLElement[] = [];

      for (let c = 0; c < this.cols; c++) {
        const cellEl = document.createElement("div");
        cellEl.setAttribute("role", "gridcell");
        cellEl.dataset["row"] = String(r);
        cellEl.dataset["col"] = String(c);
        cellEl.tabIndex = r === 0 && c === 0 ? 0 : -1;
        this.renderCell(state.grid[r][c], r, c, state.phase, cellEl);
        rowEl.appendChild(cellEl);
        rowCells.push(cellEl);
      }

      this.container.appendChild(rowEl);
      this.cellElements.push(rowCells);
    }
  }

  // ─── Cell Rendering ──────────────────────────────────────────────────────────

  private renderCell(
    cell: Cell,
    row: number,
    col: number,
    phase: GamePhase,
    el?: HTMLElement
  ): void {
    const cellEl = el ?? this.cellElements[row]?.[col];
    if (!cellEl) return;

    const state = this.getCellVisualState(cell, phase);
    cellEl.className = `board-cell board-cell--${state}`;

    if (cell.isRevealed && cell.adjacentMines === -1) {
      cellEl.classList.add("board-cell--triggered");
    }
    if (cell.adjacentMines > 0 && cell.isRevealed && !cell.isMine) {
      cellEl.classList.add(`board-cell--count-${cell.adjacentMines}`);
    }

    cellEl.innerHTML = this.getCellContent(cell, state);
    cellEl.setAttribute("aria-label", this.getCellAriaLabel(cell, row, col));
    cellEl.setAttribute("aria-pressed", cell.isFlagged ? "true" : "false");

    const interactive = phase === "PLAY" || phase === "IDLE";
    cellEl.setAttribute("aria-disabled", interactive ? "false" : "true");
    cellEl.tabIndex = interactive
      ? row === this.focusRow && col === this.focusCol ? 0 : -1
      : -1;
  }

  private getCellVisualState(cell: Cell, phase: GamePhase): string {
    if (cell.isRevealed) {
      if (cell.isMine) {
        return cell.adjacentMines === -1 ? "triggered" : "mine";
      }
      return cell.adjacentMines === 0 ? "safe-empty" : "safe-count";
    }
    if (cell.isFlagged) {
      if ((phase === "LOSE" || phase === "WIN") && !cell.isMine) return "flag-wrong";
      return "flagged";
    }
    return "hidden";
  }

  private getCellContent(cell: Cell, state: string): string {
    // Equipped cosmetic theme may swap flag/mine SVGs for emoji glyphs.
    const theme = getActiveTheme();
    const flagGlyph = theme.icons.flag
      ? `<span class="cell-emoji">${theme.icons.flag}</span>`
      : FLAG_SVG;
    const mineGlyph = theme.icons.mine
      ? `<span class="cell-emoji">${theme.icons.mine}</span>`
      : MINE_SVG;

    switch (state) {
      case "flagged":
        return `<span class="cell-icon cell-icon--flag">${flagGlyph}</span>`;
      case "flag-wrong":
        return `<span class="cell-icon cell-icon--wrong">${flagGlyph}<span class="cell-icon__cross">${WRONG_FLAG_SVG}</span></span>`;
      case "mine":
        return `<span class="cell-icon cell-icon--mine">${mineGlyph}</span>`;
      case "triggered":
        return `<span class="cell-icon cell-icon--triggered">${mineGlyph}</span>`;
      case "safe-count":
        return `<span class="cell-number" aria-hidden="true">${cell.adjacentMines}</span>`;
      default:
        return "";
    }
  }

  private getCellAriaLabel(cell: Cell, row: number, col: number): string {
    const pos = `row ${row + 1}, column ${col + 1}`;
    if (!cell.isRevealed) {
      return cell.isFlagged ? `Flagged, ${pos}` : `Unrevealed, ${pos}`;
    }
    if (cell.isMine) return `Mine, ${pos}`;
    if (cell.adjacentMines === 0) return `Safe, no adjacent mines, ${pos}`;
    return `Safe, ${cell.adjacentMines} adjacent mine${cell.adjacentMines === 1 ? "" : "s"}, ${pos}`;
  }

  private updateInteractivity(phase: GamePhase): void {
    const interactive = phase === "PLAY" || phase === "IDLE";
    this.container.classList.toggle("board-grid--inactive", !interactive);
  }

  // ─── Event Binding ───────────────────────────────────────────────────────────

  private bindContainerEvents(): void {
    this.container.addEventListener("pointerdown",   this.handlePointerDown,   { passive: false });
    this.container.addEventListener("pointermove",   this.handlePointerMove,   { passive: true });
    this.container.addEventListener("pointerup",     this.handlePointerUp,     { passive: false });
    this.container.addEventListener("pointercancel", this.handlePointerCancel, { passive: true });

    // Suppress Safari context menu on long-press AND handle desktop right-click flag
    this.container.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      if (this.activePointerId === null) {
        const coords = this.cellCoordsFromElement(e.target as HTMLElement);
        if (coords && this.phase !== "WIN" && this.phase !== "LOSE") {
          this.callbacks.onFlag(coords[0], coords[1]);
          this.callbacks.onSound?.("flag");
        }
      }
    });

    // Suppress iOS callout / text selection on long-press
    this.container.addEventListener("selectstart", (e) => e.preventDefault());

    // Keyboard nav
    this.container.addEventListener("keydown", this.handleKeyDown);

    // Pencil hover highlight — pure progressive enhancement; browsers
    // without pen hover simply never fire these.
    this.container.addEventListener("pointermove", this.handleHoverMove, { passive: true });
    this.container.addEventListener("pointerleave", this.clearHover, { passive: true });
  }

  private handleHoverMove = (e: PointerEvent): void => {
    // Only a hovering pen (no contact, no active press) highlights.
    if (e.pointerType !== "pen" || e.buttons !== 0 || this.activePointerId !== null) {
      return;
    }
    const coords = this.cellCoordsFromElement(e.target as HTMLElement);
    const cellEl = coords ? this.cellElements[coords[0]]?.[coords[1]] ?? null : null;
    if (cellEl === this.hoverCellEl) return;

    this.hoverCellEl?.classList.remove("board-cell--hover");
    this.hoverCellEl = cellEl;
    cellEl?.classList.add("board-cell--hover");
  };

  private clearHover = (): void => {
    this.hoverCellEl?.classList.remove("board-cell--hover");
    this.hoverCellEl = null;
  };

  // ─── Pointer Handlers ────────────────────────────────────────────────────────

  private handlePointerDown = (e: PointerEvent): void => {
    // Only track one pointer at a time
    if (this.activePointerId !== null) return;

    // Mouse: only left button — right-click handled by contextmenu
    if (e.pointerType === "mouse" && e.button !== 0) return;

    // ── CRITICAL FIX: resolve coords FIRST before preventDefault ──
    // If coords aren't found (click landed outside a cell), bail early.
    // Calling preventDefault before this caused pencil taps on child
    // elements (SVG icons, number spans) to be swallowed before the
    // cell walk-up could complete.
    const coords = this.cellCoordsFromElement(e.target as HTMLElement);
    if (!coords) return;

    // Now safe to prevent default for pen/touch
    if (e.pointerType !== "mouse") {
      e.preventDefault();
    }

    // Stylus barrel button (third-party pens report it as the secondary
    // button) — flag instantly, no hold needed.
    if (e.pointerType === "pen" && (e.buttons & 2) !== 0) {
      if (this.phase !== "WIN" && this.phase !== "LOSE") {
        this.callbacks.onFlag(coords[0], coords[1]);
        this.callbacks.onSound?.("flag");
        hapticMedium();
      }
      return;
    }

    this.activePointerId  = e.pointerId;
    this.pressStartCell   = coords;
    this.pressStartX      = e.clientX;
    this.pressStartY      = e.clientY;
    this.pressMoved       = false;

    // Capture pointer for reliable move/up events
    try { this.container.setPointerCapture(e.pointerId); } catch { /* ignore */ }

    // Mouse resolves on pointerup — nothing more to do here
    if (e.pointerType === "mouse") return;

    // Finger + Pencil: pressed-down visual feedback only.
    // Flagging is a mode (Dig/Flag action bar), not a hold — the old
    // 500ms long-press gesture is gone.
    const cellEl = this.cellElements[coords[0]]?.[coords[1]];
    cellEl?.classList.add("board-cell--pressing");
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;

    const dx = Math.abs(e.clientX - this.pressStartX);
    const dy = Math.abs(e.clientY - this.pressStartY);

    if (dx > MOVE_THRESHOLD_PX || dy > MOVE_THRESHOLD_PX) {
      this.pressMoved = true;
      this.clearPressingVisual();
    }
  };

  private handlePointerUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    if (e.pointerType !== "mouse") e.preventDefault();

    try { this.container.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

    if (e.pointerType === "mouse") {
      if (!this.pressMoved && this.pressStartCell && e.button === 0) {
        const [row, col] = this.pressStartCell;
        if (this.phase !== "WIN" && this.phase !== "LOSE") {
          this.callbacks.onReveal(row, col);
        }
      }
      this.resetPressState();
      return;
    }

    // Finger / Pencil: a clean tap resolves by the active mode —
    // Dig reveals, Flag toggles a flag (mobile action bar).
    if (!this.pressMoved && this.pressStartCell) {
      const [row, col] = this.pressStartCell;
      if (this.phase !== "WIN" && this.phase !== "LOSE") {
        if (this.flagMode) {
          this.callbacks.onFlag(row, col);
          this.callbacks.onSound?.("flag");
          hapticMedium();
        } else {
          this.callbacks.onReveal(row, col);
        }
      }
    }

    this.clearPressingVisual();
    this.resetPressState();
  };

  private handlePointerCancel = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    this.clearPressingVisual();
    this.resetPressState();
  };

  private clearPressingVisual(): void {
    if (this.pressStartCell) {
      const [r, c] = this.pressStartCell;
      this.cellElements[r]?.[c]?.classList.remove("board-cell--pressing");
    }
  }

  private resetPressState(): void {
    this.pressStartCell   = null;
    this.activePointerId  = null;
    this.pressMoved       = false;
  }

  // ─── Keyboard Handler ─────────────────────────────────────────────────────────

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (this.phase === "WIN" || this.phase === "LOSE") return;

    const { focusRow, focusCol } = this;
    let newRow  = focusRow;
    let newCol  = focusCol;
    let handled = true;

    switch (e.key) {
      case "ArrowUp":    newRow = Math.max(0, focusRow - 1); break;
      case "ArrowDown":  newRow = Math.min(this.rows - 1, focusRow + 1); break;
      case "ArrowLeft":  newCol = Math.max(0, focusCol - 1); break;
      case "ArrowRight": newCol = Math.min(this.cols - 1, focusCol + 1); break;
      case "Enter":      this.callbacks.onReveal(focusRow, focusCol); return;
      case "Space":
      case " ":          this.callbacks.onFlag(focusRow, focusCol); break;
      case "Home":       newRow = 0; newCol = 0; break;
      case "End":        newRow = this.rows - 1; newCol = this.cols - 1; break;
      default:           handled = false;
    }

    if (handled) {
      e.preventDefault();
      this.setFocusedCell(newRow, newCol);
    }
  };

  // ─── Focus Management ─────────────────────────────────────────────────────────

  private setFocusedCell(row: number, col: number): void {
    const prev = this.cellElements[this.focusRow]?.[this.focusCol];
    if (prev) prev.tabIndex = -1;

    this.focusRow = row;
    this.focusCol = col;

    const next = this.cellElements[row]?.[col];
    if (next) {
      next.tabIndex = 0;
      next.focus({ preventScroll: false });
    }
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────

  private cellCoordsFromElement(el: HTMLElement | null): [number, number] | null {
    let target: HTMLElement | null = el;
    while (target && target !== this.container) {
      if (
        target.dataset["row"] !== undefined &&
        target.dataset["col"] !== undefined
      ) {
        return [
          parseInt(target.dataset["row"], 10),
          parseInt(target.dataset["col"], 10),
        ];
      }
      target = target.parentElement;
    }
    return null;
  }

  private shakeGrid(): void {
    this.container.classList.remove("board-grid--shake");
    void this.container.offsetWidth;
    this.container.classList.add("board-grid--shake");
    this.container.addEventListener(
      "animationend",
      () => this.container.classList.remove("board-grid--shake"),
      { once: true }
    );
  }
}
